import { CalendarSelfHealingManager } from './self-healing-manager';
import { CalendarConnectionManager } from './connection-manager';

export class CalendarBackgroundServices {
  private static instance: CalendarBackgroundServices;
  private isStarted = false;

  static getInstance(): CalendarBackgroundServices {
    if (!this.instance) {
      this.instance = new CalendarBackgroundServices();
    }
    return this.instance;
  }

  start(): void {
    if (this.isStarted) {
      console.log('🔄 Calendar background services already running');
      return;
    }

    console.log('🚀 Starting all calendar background services...');

    try {
      // Start the self-healing manager
      const selfHealingManager = CalendarSelfHealingManager.getInstance();
      selfHealingManager.start();

      // Note: Proactive refresh is now handled server-side via Edge Functions
      // Frontend should not try to query all users' calendar connections
      console.log('ℹ️ Proactive token refresh is handled server-side via Supabase Edge Functions');

      // Clear any stale cache
      CalendarConnectionManager.clearCache();

      this.isStarted = true;
      console.log('✅ All calendar background services started successfully');

      // Log the comprehensive monitoring setup
      console.log('📊 Calendar monitoring setup:', {
        selfHealing: 'Active (checks every 2 minutes)',
        proactiveRefresh: 'Server-side via Edge Functions',
        validationCache: '5-minute TTL',
        gracePeriod: '2 hours for token expiry',
        failureThreshold: '50+ failures for catastrophic status',
        deactivationThreshold: 'Only truly catastrophic failures'
      });

    } catch (error) {
      console.error('❌ Error starting calendar background services:', error);
    }
  }

  stop(): void {
    if (!this.isStarted) {
      return;
    }

    console.log('🛑 Stopping all calendar background services...');

    try {
      // Stop the self-healing manager
      const selfHealingManager = CalendarSelfHealingManager.getInstance();
      selfHealingManager.stop();

      // Clear cache
      CalendarConnectionManager.clearCache();

      this.isStarted = false;
      console.log('✅ All calendar background services stopped');

    } catch (error) {
      console.error('❌ Error stopping calendar background services:', error);
    }
  }

  // Public method to force immediate healing
  async forceHeal(): Promise<void> {
    console.log('🚨 Force healing requested for all calendar services...');
    
    const selfHealingManager = CalendarSelfHealingManager.getInstance();
    await selfHealingManager.forceHeal();
  }

  // Get status of all services
  getStatus(): {
    isStarted: boolean;
    services: {
      selfHealing: boolean;
      proactiveRefresh: string;
    };
  } {
    return {
      isStarted: this.isStarted,
      services: {
        selfHealing: this.isStarted,
        proactiveRefresh: 'Server-side via Edge Functions'
      }
    };
  }
}